/* eslint-disable max-lines */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import _ from 'lodash';
import { observer } from 'mobx-react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { Modal, ModalBody, ModalHeader } from '@this/src/components/shared/ui/feedbacks/modal';
import type { ForbiddenRepositoryArgs } from '@this/domain/organization/organization3';
import type { ShowOrganization } from '@this/src/api/arrangement/organization_api';
import { RevenueReferenceDateTypes } from '@this/domain/organization/organization3';
import { allAvailableRequestForms } from '@this/src/domain/available_request_form';
import { isAiTravel } from '@this/src/util/utils';
import { Loading } from '@this/src/components/shared/ui/feedbacks/loading';
import type ForbiddenRequestForm from '@this/src/domain/organization/forbidden_request_form';
import { hasOptionPlan } from '@this/src/domain/organization/organization_helper';
import OrganizationAPI from '@this/src/api/arrangement/organization_api';
import AvailableRepositoriesAPI from '@this/src/api/arrangement/available_repositories_api';
import MarginTypeForm from './organization/margin_type_form';
import AvailableRepositoriesForm from './organization/available_repositories_form';
import InvoiceSettingForm from './organization/invoice_setting_form';
import AvailableRequestFormsForm from './organization/available_request_forms_form';
import FareOptionsForm from './organization/fare_options_form';
import { SelectSavingAccounts } from './organization/select_saving_accounts';

interface Props {
  show: boolean;
  organizationId: number;
  onClose: () => void;
}

type AvailableRepositoriesResponse = {
  category: string;
  repository: string;
};

const getAvailableReposByOrg = (
  allRepos: AvailableRepositoriesResponse[],
  forbiddenRepos: ForbiddenRepositoryArgs[] | undefined
) => {
  return allRepos.filter(
    repo => !forbiddenRepos?.find(f => f.category === repo.category && f.repository === repo.repository)
  );
};

const getAvailableRequestFormsByOrg = (forbiddenRequestForms: ForbiddenRequestForm[] | undefined) => {
  return allAvailableRequestForms.filter(arf => !forbiddenRequestForms?.find(frf => frf.category === arf));
};

const OrganizationModal: React.FC<Props> = ({ show, organizationId, onClose }) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const env =
    location?.host?.includes('localhost') || location?.host?.includes('ngrok.io')
      ? 'development'
      : location?.host?.includes('stg')
      ? 'staging'
      : 'production';
  const [availableRepos, setAvailableRepos] = useState<AvailableRepositoriesResponse[]>([]);
  const fetchAvailableRepositories = useCallback(async () => {
    try {
      const data = await AvailableRepositoriesAPI.list();
      setAvailableRepos(data);
    } catch (error) {
      console.error('Failed to fetch available repositories:', error);
    }
  }, []);
  const [loading, setLoading] = useState(false);
  const [organization, setOrganization] = useState<ShowOrganization | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchOrganization = useCallback(async () => {
    if (!organizationId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await OrganizationAPI.show(organizationId);
      setOrganization(response);
    } catch (e) {
      setError('企業情報の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    if (show && organizationId) {
      fetchOrganization();
      fetchAvailableRepositories();
    }
  }, [show, organizationId, fetchOrganization, fetchAvailableRepositories]);

  if (!show) return null;

  return (
    <Modal open={show} onClose={onClose} size="xlarge">
      {loading && <Loading />}
      {error && <p style={styles.errorMessage}>{error}</p>}
      {organization && !loading && (
        <>
          <ModalHeader>企業情報</ModalHeader>
          <ModalBody style={styles.wrapper}>
            <div style={styles.left}>
              <div style={styles.inputWrapper}>
                <label>ID</label>
                <span>{organization.id}</span>
              </div>
              <div style={styles.inputWrapper}>
                <label>名前</label>
                <span>{organization.name || ''}</span>
              </div>
              <div style={styles.inputWrapper}>
                <label>会社ホームページ</label>
                <span>{organization.url || ''}</span>
              </div>
              <div style={styles.inputWrapper}>
                <label>AmadeusName</label>
                <span>{organization.amadeusCompanyName || ''}</span>
              </div>
              <div style={styles.inputWrapper}>
                <label>住所</label>
                <span>{organization.address || ''}</span>
              </div>
              <div style={styles.inputWrapper}>
                <label>電話番号</label>
                <span>{organization.tel || ''}</span>
              </div>
              <div style={styles.inputWrapper}>
                <label>担当者名</label>
                <span>{organization.personInChargeName || ''}</span>
              </div>
              <div style={styles.inputWrapper}>
                <label>担当者メールアドレス</label>
                <span>{organization.personInChargeEmail || ''}</span>
              </div>
              <div style={styles.inputWrapper}>
                <label>【旧】請求先メールアドレス</label>
                <span>{organization.billingEmail || ''}</span>
              </div>
              <div style={styles.inputWrapper}>
                <label>請求書送付先</label>
                {organization.organizationBillReceivers?.map((r, i) => (
                  <div key={i} className="flex" style={{ alignItems: 'center' }}>
                    <label style={{ minWidth: '40px' }}>宛名</label>
                    <input type="text" value={r.name || ''} />
                    <label style={{ minWidth: '50px' }}>メール</label>
                    <input type="text" value={r.email || ''} />
                  </div>
                ))}
              </div>
              <div style={styles.inputWrapper}>
                <label>お客様口座情報</label>
                <span>{organization.bankAccountInfo || ''}</span>
              </div>
              {hasOptionPlan(organization.currentOptionPlanPrices, '請求書分割') && (
                <div style={styles.inputWrapper}>
                  <label>請求書分割</label>
                  <div style={styles.radioButtonGroup}>
                    <label className="radio">
                      <input
                        type="radio"
                        value="invoice_split_target"
                        checked={organization.invoiceSplitType === 'invoice_split_target'}
                        disabled
                      />
                      請求先マスタ
                    </label>
                    <label className="radio">
                      <input
                        type="radio"
                        value="none"
                        checked={organization.invoiceSplitType === 'none'}
                        disabled
                      />
                      利用しない
                    </label>
                  </div>
                </div>
              )}
              <div style={styles.inputWrapper}>
                <label>入金口座設定</label>
                <SelectSavingAccounts value={organization.savingAccountId || null} />
              </div>
              <div style={styles.inputWrapper}>
                <label>当座対象銘柄</label>
                <label>
                  <input type="checkbox" checked={organization.allowOverdraft} disabled />
                  当座対象銘柄
                </label>
              </div>
              {isAiTravel(organization.serviceId) && (
                <div style={styles.inputWrapper}>
                  <label>BtoBプラットフォーム連携</label>
                  <div style={styles.infomartWrapper}>
                    <label>発行先コード</label>
                    <span>{organization.infomartPrivateCustCd || ''}</span>

                    <label>振込先コード</label>
                    <span>{organization.infomartTransferCode || ''}</span>
                    <div style={styles.informationPanel}>
                      <div style={styles.labelState}>
                        {!organization?.infomartApiToken?.customerMngNum ? '未連携' : '連携中'}
                      </div>
                    </div>
                    <label>顧客管理番号：{organization?.infomartApiToken?.customerMngNum}</label>
                    <label>会社名：{organization?.infomartApiToken?.companyNameOrg}</label>
                  </div>
                </div>
              )}
              <div style={styles.inputWrapper}>
                <label>Salesforce ID</label>
                <span>{organization.salesforceId || ''}</span>
              </div>
              <div style={styles.inputWrapper}>
                <label>外部ID(Import)</label>
                <span>{organization.salesforceImportId || ''}</span>
              </div>
              <div style={styles.inputWrapper}>
                <label>法人番号</label>
                <span>{organization.enterpriseIdentificationNumber || ''}</span>
              </div>
              <div style={styles.inputWrapper}>
                <label>業種(大分類)</label>
                <span>{organization.segment1 || ''}</span>
              </div>
              <div style={styles.inputWrapper}>
                <label>業種(中分類)</label>
                <span>{organization.segment2 || ''}</span>
              </div>
              <div style={styles.inputWrapper}>
                <label>企業規模</label>
                <span>{organization.companySize || ''}</span>
              </div>
              <div style={styles.inputWrapper}>
                <label>出張者ダッシュボードラベル(デフォルト値: 検索から始める)</label>
                <span>{organization.userDashboardLabel1 || ''}</span>
              </div>
              <div style={styles.inputWrapper}>
                <label>出張者ダッシュボードラベル(デフォルト値: 申請から始める)</label>
                <span>{organization.userDashboardLabel2 || ''}</span>
              </div>
              <div style={styles.inputWrapper}>
                <label>出張者ダッシュボードラベル(デフォルト値: EXIC利用時の簡易申請)</label>
                <span>{organization.userDashboardLabel3 || ''}</span>
              </div>
              <div style={styles.inputWrapper}>
                <label>社員数</label>
                <span>{organization.memberCount}</span>
              </div>
              <div style={styles.inputWrapper}>
                <label>管理者数</label>
                <span>{organization.adminCount}</span>
              </div>
              <div style={styles.inputWrapper}>
                <label>amadeus 法人運賃ID</label>
                <FareOptionsForm organization={organization} />
              </div>
              <div style={styles.inputWrapper}>
                <label>自社フラグ</label>
                <input type="checkbox" checked={!!organization.isOwnCompany} disabled />
              </div>
              {isAiTravel(organization.serviceId) && (
                <div style={styles.inputWrapper}>
                  <label>AIコンシェルジュ</label>
                  <input type="checkbox" checked={organization.useChatbot} disabled />
                </div>
              )}
            </div>
            <div style={styles.right}>
              <div style={styles.inputWrapper}>
                <label>システム利用料決済方法</label>
                <div style={styles.radioButtonGroup}>
                  <label className="radio">
                    <input
                      type="radio"
                      value="invoice"
                      checked={organization.systemFeeSetting?.paymentMethodType === 'invoice'}
                      disabled
                    />
                    請求書
                  </label>
                  <label className="radio">
                    <input
                      type="radio"
                      value="credit_card"
                      checked={organization.systemFeeSetting?.paymentMethodType === 'credit_card'}
                      disabled
                    />
                    クレジットカード
                  </label>
                </div>
              </div>
              <div style={styles.inputWrapper}>
                <label>旅程決済方法</label>
                <div style={styles.radioButtonGroup}>
                  <label className="radio">
                    <input
                      type="radio"
                      value="invoice"
                      checked={organization.paymentMethodType === 'invoice'}
                      disabled
                    />
                    請求書
                  </label>
                  <label className="radio">
                    <input
                      type="radio"
                      value="card"
                      checked={organization.paymentMethodType === 'card'}
                      disabled
                    />
                    クレジットカード
                  </label>
                  <label className="radio">
                    <input
                      type="radio"
                      value="selectable"
                      checked={organization.paymentMethodType === 'selectable'}
                      disabled
                    />
                    予約時に選択
                  </label>
                </div>
              </div>
              {/*  TODO: ソニーペイメントをprodで有効化するときにenv切り分けを削除  */}
              {isAiTravel(organization.serviceId) &&
              (env === 'development' || env === 'staging') &&
              (organization.paymentMethodType === 'card' || organization.paymentMethodType === 'selectable') ? (
                <div style={styles.inputWrapper}>
                  <label>クレジットカード決済ゲートウェイ</label>
                  <div style={styles.radioButtonGroup}>
                    <label className="radio">
                      <input
                        type="radio"
                        value="gmo"
                        checked={organization.paymentGatewayType === 'gmo'}
                        disabled
                      />
                      GMOペイメント
                    </label>
                    <label className="radio">
                      <input
                        type="radio"
                        value="sony"
                        checked={organization.paymentGatewayType === 'sony'}
                        disabled
                      />
                      ソニーペイメント
                    </label>
                  </div>
                </div>
              ) : (
                ''
              )}
              <div style={styles.inputWrapper}>
                <label>AI予約を利用する</label>
                <input type="checkbox" checked={organization.aiReservationEnabled} disabled />
              </div>
              <div style={styles.inputWrapper}>
                <label>手数料表記</label>
                <input type="checkbox" checked={organization.showFee} disabled />
              </div>
              <div style={styles.inputWrapper}>
                <label>経路検索</label>
                <div style={styles.radioButtonGroup}>
                  <label className="radio">
                    <input
                      type="radio"
                      value="legacy"
                      checked={organization.transitSearchApi === 'legacy'}
                      disabled
                    />
                    レガシー版
                  </label>
                  <label className="radio">
                    <input
                      type="radio"
                      value="ekispert"
                      checked={organization.transitSearchApi === 'ekispert'}
                      disabled
                    />
                    新版
                    <span style={{ fontSize: '9px' }}>
                      ※新版のご利用の際は、別途Transfer Data社に申込みが必要ですのでお問い合わせください。
                    </span>
                  </label>
                </div>
              </div>
              {organization.transitSearchApi === 'ekispert' && (
                <div style={styles.inputWrapper}>
                  <label>
                    <input type="checkbox" checked={organization.ekispertDisplayLimited} disabled />
                    AI Travelで予約可能な商品のみ表示
                  </label>
                </div>
              )}
              {isAiTravel(organization.serviceId) && (
                <div style={styles.inputWrapper}>
                  <label>国内航空券自動予約システム</label>
                  <div style={styles.radioButtonGroup}>
                    <label className="radio">
                      <input
                        type="radio"
                        value="smart_ticket"
                        checked={organization.transportElementReservationApi === 'smart_ticket'}
                        disabled
                      />
                      スマチケ
                    </label>
                    <label className="radio">
                      <input
                        type="radio"
                        value="ticket_opr"
                        checked={organization.transportElementReservationApi === 'ticket_opr'}
                        disabled
                      />
                      TicketOpr
                    </label>
                  </div>
                </div>
              )}
              <div style={styles.inputWrapper}>
                <label>表示商材</label>
                {availableRepos ? (
                  <AvailableRepositoriesForm
                    currentRepos={getAvailableReposByOrg(availableRepos, organization?.forbiddenRepositories)}
                    selectableRepos={availableRepos}
                  />
                ) : (
                  ''
                )}
              </div>
              <div style={styles.inputWrapper}>
                <label>ホテル税込表示</label>
                <input type="checkbox" checked={organization.includeTax} disabled />
              </div>
              <div style={styles.inputWrapper}>
                <label>ホテル最安値表示</label>
                <input type="checkbox" checked={!!organization.hotelSortType} disabled />
              </div>
              {isAiTravel(organization.serviceId) && (
                <>
                  <div style={styles.inputWrapper}>
                    <label>楽天ホテル自動予約</label>
                    <input type="checkbox" checked={organization.enableAutoReserveRakutenHotel} disabled />
                  </div>
                  <div style={styles.inputWrapper}>
                    <label>TicketOprの自動発券</label>
                    <input type="checkbox" checked={organization?.enableTicketOprAutoTicketing} disabled />
                  </div>
                </>
              )}
              <div style={styles.inputWrapper}>
                <label>JR九州きっぷ表示</label>
                <input type="checkbox" checked={organization.useKyuusyuuTicket} disabled />
              </div>
              <div style={styles.inputWrapper}>
                <label>レンタカーフォーム表示</label>
                <input type="checkbox" checked={organization.enableRentalcarForm} disabled />
              </div>
              <div style={styles.inputWrapper}>
                <label>土日祝日を営業日としてカウントする</label>
                <input type="checkbox" checked={organization.dayOffOperation} disabled />
              </div>
              <div style={styles.inputWrapper}>
                <label>新幹線期限</label>
                <div className="flex">
                  <select value={organization?.shinkansenLimitType?.businessDays} disabled>
                    {_.range(1, 8).map((value, index) => (
                      <option key={index} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                  <span>営</span>
                  <select value={organization?.shinkansenLimitType?.hour} disabled>
                    {_.range(0, 24).map((value, index) => (
                      <option key={index} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                  <span>時</span>
                </div>
              </div>
              <div style={styles.inputWrapper}>
                <label>自動予約対象外ホテル期限</label>
                <div className="flex">
                  <select value={organization?.hotelLimitType?.businessDays} disabled>
                    {_.range(1, 8).map((value, index) => (
                      <option key={index} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                  <span>営</span>
                  <select value={organization?.hotelLimitType?.hour} disabled>
                    {_.range(0, 24).map((value, index) => (
                      <option key={index} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                  <span>時</span>
                </div>
              </div>
              <div style={styles.inputWrapper}>
                <label>国内航空券依頼期限</label>
                <div className="flex">
                  <select value={organization?.domesticAirLimitType?.businessDays} disabled>
                    {_.range(1, 8).map((value, index) => (
                      <option key={index} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                  <span>営</span>
                  <select value={organization?.domesticAirLimitType?.hour} disabled>
                    {_.range(0, 24).map((value, index) => (
                      <option key={index} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                  <span>時</span>
                </div>
              </div>
              <div style={styles.inputWrapper}>
                <label>レンタカー期限</label>
                <div className="flex">
                  <select value={organization?.rentalCarLimitType?.businessDays} disabled>
                    {_.range(1, 8).map((value, index) => (
                      <option key={index} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                  <span>営</span>
                  <select value={organization?.rentalCarLimitType?.hour} disabled>
                    {_.range(0, 24).map((value, index) => (
                      <option key={index} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                  <span>時</span>
                </div>
              </div>
              <div style={styles.inputWrapper}>
                <label>飛行機パッケージ期限</label>
                <div className="flex">
                  <select value={organization?.packageLimitType?.businessDays} disabled>
                    {_.range(1, 21).map((value, index) => (
                      <option key={index} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                  <span>営</span>
                  <select value={organization?.packageLimitType?.hour} disabled>
                    {_.range(0, 24).map((value, index) => (
                      <option key={index} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                  <span>時</span>
                </div>
              </div>
              <div style={styles.inputWrapper}>
                <label>鉄道パッケージ期限</label>
                <div className="flex">
                  <select value={organization?.railwayPackageLimitType?.businessDays} disabled>
                    {_.range(1, 21).map((value, index) => (
                      <option key={index} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                  <span>営</span>
                  <select value={organization?.railwayPackageLimitType?.hour} disabled>
                    {_.range(0, 24).map((value, index) => (
                      <option key={index} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                  <span>時</span>
                </div>
              </div>
              <div style={styles.inputWrapper}>
                <label>リクエストフォーム依頼期限</label>
                <div className="flex">
                  <select value={organization?.arrangementRequestLimitType?.businessDays} disabled>
                    {_.range(1, 21).map((value, index) => (
                      <option key={index} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                  <span>営</span>
                  <select value={organization?.arrangementRequestLimitType?.hour} disabled>
                    {_.range(0, 24).map((value, index) => (
                      <option key={index} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                  <span>時</span>
                </div>
              </div>
              <div style={styles.inputWrapper}>
                <label>新幹線QRチケット利用可否</label>
                <input type="checkbox" checked={organization.useQr} disabled />
              </div>
              <div style={styles.inputWrapper}>
                <label>EXIC予約依頼可否</label>
                <input type="checkbox" checked={!!organization.showEx} disabled />
              </div>
              <div style={styles.inputWrapper}>
                <label>EXIC基本会員番号</label>
                <span>{organization.exicBasicMemberNo || ''}</span>
              </div>
              <div style={styles.inputWrapper}>
                <label>EXIC連携の種類</label>
                <div style={styles.radioButtonGroup}>
                  <label className="radio">
                    <input
                      type="radio"
                      value="card_based"
                      checked={organization.exicUsageMode === 'card_based'}
                      disabled
                    />
                    カードにユーザが紐づく
                  </label>
                  <label className="radio">
                    <input
                      type="radio"
                      value="user_based"
                      checked={organization.exicUsageMode === 'user_based'}
                      disabled
                    />
                    ユーザにカードが紐づく
                  </label>
                </div>
              </div>
              {organization.exicUsageMode === 'card_based' && (
                <div style={styles.inputWrapper}>
                  <label>ユーザーによるEXIC情報入力を許可する</label>
                  <input type="checkbox" checked={organization?.allowUserExicInput} disabled />
                </div>
              )}
              {utils.isTabikobo(organization.serviceId) && (
                <div style={styles.inputWrapper}>
                  <label>EXIC整理番号による紐づけを有効にする</label>
                  <input type="checkbox" checked={organization?.useExicReferenceNumber} disabled />
                </div>
              )}
              <div style={styles.inputWrapper}>
                <label>申請から始める機能を有効にする</label>
                <input type="checkbox" checked={!!organization.useNonOrderItem} disabled />
              </div>
              <div style={styles.inputWrapper}>
                <label>簡易申請を有効にする</label>
                <input type="checkbox" checked={organization.showExicSeisanTrips} disabled />
              </div>
              <div style={styles.inputWrapper}>
                <label>過去日の申請可否</label>
                <input type="checkbox" checked={organization?.allowApplyPastDate} disabled />
              </div>
              <div style={styles.inputWrapper}>
                <label>予約依頼に関するメール通知対象者</label>
                <div style={styles.radioButtonGroup}>
                  <label className="radio">
                    <input
                      type="radio"
                      value="toggle"
                      checked={organization.reserveRequestMailNotificationType === 'togglable'}
                      disabled
                    />
                    関係者通知を 有効/無効 選択する
                  </label>
                  <label className="radio">
                    <input
                      type="radio"
                      value="select_user"
                      checked={organization.reserveRequestMailNotificationType === 'selectable_user'}
                      disabled
                    />
                    社員マスタより選択する
                  </label>
                </div>
              </div>
              <div style={styles.inputWrapper}>
                <label>旅程作成フォームでSmart Search使用</label>
                <input type="checkbox" checked={organization.useSmartSearch} disabled />
              </div>
              {organization.marginTypes.map((marginType, i) => (
                <div style={styles.inputWrapper} key={i}>
                  <MarginTypeForm marginType={marginType} />
                </div>
              ))}
              <div style={styles.inputWrapper}>
                <label>楽天フィー（％）</label>
                <span>{organization.rakutenInnerFeeRate || ''}</span>
              </div>
              {hasOptionPlan(organization.currentOptionPlanPrices, 'Express精算委託') && (
                <div style={styles.inputWrapper}>
                  <label>EXIC請求おまとめ手数料（％）</label>
                  <input type="text" value={organization.exicAgencyFeeRate || ''} disabled />
                </div>
              )}
              <div style={styles.inputWrapper}>
                <label>ビジネスえきねっと法人番号</label>
                <input type="text" value={organization.businessEkinetCorporateInquiryNumber || ''} disabled />
              </div>
              <div style={styles.inputWrapper}>
                <label>ビジネスえきねっと請求おまとめ手数料率（％）</label>
                <input type="text" value={organization.businessEkinetAgencyFeeRate || ''} disabled />
              </div>
              <div style={styles.inputWrapper}>
                <label>売上基準日</label>
                <select value={organization.revenueReferenceDateType} disabled>
                  {_.keys(RevenueReferenceDateTypes).map(key => (
                    <option key={key} value={key}>
                      {RevenueReferenceDateTypes[key as keyof typeof RevenueReferenceDateTypes]}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.inputWrapper}>
                <label>出張事前申請のワークフローのルート設定</label>
                <div style={styles.radioButtonGroup}>
                  <label className="radio">
                    <input
                      type="radio"
                      value="department"
                      checked={organization.workflowStyle === 'department'}
                      disabled
                    />
                    部署ごとにワークフローを設定する
                  </label>
                  <label className="radio">
                    <input type="radio" value="trip" checked={organization.workflowStyle === 'trip'} disabled />
                    旅程ごとにワークフローを設定する
                  </label>
                </div>
              </div>
              <div style={styles.inputWrapper}>
                <label>出張報告ワークフローのルート設定</label>
                <div style={styles.radioButtonGroup}>
                  <label className="radio">
                    <input
                      type="radio"
                      value="project"
                      checked={
                        organization.tripReportWorkflowStyle === 'project' ||
                        organization.tripReportWorkflowStyle === 'department'
                      }
                      disabled
                    />
                    プロジェクトごとにワークフローを設定する
                  </label>
                  <label className="radio">
                    <input
                      type="radio"
                      value="trip_report"
                      checked={organization.tripReportWorkflowStyle === 'trip_report'}
                      disabled
                    />
                    出張報告ごとにワークフローを設定する
                  </label>
                </div>
              </div>
              <div style={styles.inputWrapper}>
                <label>承認対象在庫の自動却下</label>
                <label>
                  <input type="checkbox" checked={organization.enableAutoStockCheck} disabled />
                  有効
                </label>
              </div>
              <div style={styles.inputWrapper}>
                <label>事前申請一括承認・却下</label>
                <label>
                  <input type="checkbox" checked={organization.enableBatchApproval} disabled />
                  有効
                </label>
              </div>
              <div style={styles.inputWrapper}>
                <label>リクエストフォーム利用</label>
                <label>
                  <input type="checkbox" checked={organization.arrangementRequestSetting?.enable} disabled />
                  有効
                </label>
              </div>
              <div style={styles.inputWrapper}>
                <label>リクエストフォーム依頼手数料</label>
                <span>{organization.arrangementRequestSetting?.fee || ''}</span>
              </div>
              <div style={styles.inputWrapper}>
                <label>リクエストフォーム承認</label>
                <label>
                  <input type="checkbox" checked={!!organization.requestFormApprovalRequired} disabled />
                  有効
                </label>
              </div>
              <div style={styles.inputWrapper}>
                <label>リクエストフォーム表示商材</label>
                <AvailableRequestFormsForm
                  currentRequestForms={getAvailableRequestFormsByOrg(organization?.forbiddenRequestForms)}
                />
              </div>
              <div style={styles.inputWrapper}>
                <label>出入国カード作成代行表示</label>
                <input type="checkbox" checked={organization.immigrationCardEnabled} disabled />
              </div>
              <div style={styles.inputWrapper}>
                <label>ビザ申請代行表示</label>
                <input type="checkbox" checked={organization.visaApplicationEnabled} disabled />
              </div>
              {organization.invoiceSetting && (
                <div style={styles.inputWrapper}>
                  <label>請求書設定</label>
                  <InvoiceSettingForm setting={organization.invoiceSetting} />
                </div>
              )}
            </div>
          </ModalBody>
        </>
      )}
    </Modal>
  );
};

const getStyles = (theme: AITTheme): { [key: string]: React.CSSProperties } => ({
  wrapper: {
    display: 'flex'
  },
  right: {
    flex: '999 1 200px'
  },
  left: {
    flex: '999 1 200px',
    marginRight: 20
  },
  inputWrapper: {
    marginBottom: 10
  },
  radioButtonGroup: {
    display: 'flex',
    alignItems: 'center'
  },
  infomartWrapper: {
    border: '1px solid #ccc',
    padding: 10,
    borderRadius: 4
  },
  informationPanel: {
    display: 'flex',
    alignItems: 'center'
  },
  labelState: {
    fontWeight: 'bold',
    padding: '8px 10px',
    borderRadius: 3,
    backgroundColor: theme.grayBorderColor
  },
  errorMessage: {
    padding: '1em 0',
    fontWeight: 'bold',
    color: 'red'
  }
});

export default observer(OrganizationModal);
